import React from 'react';
import cl from './Showcase.module.css';
import { IconGamepad, IconRobot, IconDashboard, IconCheck, IconArrowRight } from '../../components/icons';
import useReveal from '../../hooks/useReveal';

// Три блока — по одному на каждую платформу из пакета.
// Сторонами чередуем, чтобы было интереснее листать.

export default function Showcase() {
    const revealRef = useReveal();
    return (
        <section className={cl.showcase} id="platforms" ref={revealRef}>
            <div className={cl.inner}>
                <div className={cl.head}>
                    <span className={cl.eyebrow} data-reveal="down">Три платформы — один пакет</span>
                    <h2 className={cl.title} data-reveal="up" style={{ '--reveal-delay': '100ms' }}>
                        Курсы, которые дети сами просят
                    </h2>
                    <p className={cl.subtitle} data-reveal="up" style={{ '--reveal-delay': '180ms' }}>
                        Программирование, робототехника и админ-панель —
                        всё уже работает и используется в реальной школе.
                    </p>
                </div>

                {/* === Минкрафтия === */}
                <div className={cl.row} data-reveal="up" style={{ '--accent': '#3b82f6', '--accent-2': '#1e3a8a' }}>
                    <div className={cl.text}>
                        <span className={cl.platformTag} style={{ background: '#3b82f6' }}>
                            <IconGamepad width={16} height={16}/> Майнкрафтия
                        </span>
                        <h3 className={cl.rowTitle}>Программирование через Minecraft</h3>
                        <p className={cl.rowText}>
                            Дети учатся реальному программированию: Scratch, Python,
                            Pygame, Flask, JavaScript. И всё это — в антураже Minecraft
                            с собственными игровыми серверами.
                        </p>
                        <div className={cl.bullets}>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span><b>20+ курсов</b>: от Scratch до бэкенда на Flask</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span><b>30 000+ задач</b> с автопроверкой</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>5 живых <b>Minecraft-серверов</b> — мотивация играть и учиться</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>Редактор <b>Кубоша</b> (как Scratch) и движок <b>Кубикон 3D</b> (как Roblox)</span>
                            </div>
                        </div>
                        <a href="https://minecraftia-school.ru" target="_blank" rel="noopener noreferrer" className={cl.link}>
                            Посмотреть платформу <IconArrowRight width={16} height={16}/>
                        </a>
                    </div>
                    <div className={cl.visual} data-reveal="zoom" style={{ '--reveal-delay': '200ms' }}>
                        <MinecraftiaVisual/>
                    </div>
                </div>

                {/* === Роботехника === */}
                <div className={`${cl.row} ${cl.reverse}`} data-reveal="up" style={{ '--accent': '#a855f7', '--accent-2': '#6b21a8' }}>
                    <div className={cl.text}>
                        <span className={cl.platformTag} style={{ background: '#a855f7' }}>
                            <IconRobot width={16} height={16}/> Роботехника
                        </span>
                        <h3 className={cl.rowTitle}>Электроника и 3D в одной программе</h3>
                        <p className={cl.rowText}>
                            Полноценный курс электроники от резистора и закона Ома
                            до Arduino, плюс 3D-моделирование. Без необходимости
                            закупать дорогое железо в самом начале.
                        </p>
                        <div className={cl.bullets}>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span><b>50 проектов</b> на Arduino — от LED до робота</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span><b>3D-моделирование</b> в 123D Design (9 уроков)</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>Электроника: теория + симулятор схем</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>Рейтинг учеников и игровая мотивация</span>
                            </div>
                        </div>
                        <a href="https://robotehnick.ru" target="_blank" rel="noopener noreferrer" className={cl.link}>
                            Посмотреть платформу <IconArrowRight width={16} height={16}/>
                        </a>
                    </div>
                    <div className={cl.visual} data-reveal="zoom" style={{ '--reveal-delay': '200ms' }}>
                        <RobotehnickVisual/>
                    </div>
                </div>

                {/* === Админ-панель === */}
                <div className={cl.row} data-reveal="up" style={{ '--accent': '#10b981', '--accent-2': '#065f46' }}>
                    <div className={cl.text}>
                        <span className={cl.platformTag} style={{ background: '#10b981' }}>
                            <IconDashboard width={16} height={16}/> Админ-панель
                        </span>
                        <h3 className={cl.rowTitle}>Свой кабинет управления центром</h3>
                        <p className={cl.rowText}>
                            Личный кабинет для владельца центра: список учеников,
                            журнал посещений, оплаты и отчёты. Отдельный логин
                            для каждого преподавателя и доступ к группам.
                        </p>
                        <div className={cl.bullets}>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span><b>200 учеников</b> и <b>5 преподавателей</b> включены в тариф</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>Журнал посещений и оплат</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>Отчёты по группам и ученикам</span>
                            </div>
                            <div className={cl.bullet}>
                                <span className={cl.check}><IconCheck width={14} height={14} strokeWidth={2.8}/></span>
                                <span>Регистрация и блокировка учеников в один клик</span>
                            </div>
                        </div>
                        <a href="https://adminpanelminecraftia.ru" target="_blank" rel="noopener noreferrer" className={cl.link}>
                            Посмотреть админ-панель <IconArrowRight width={16} height={16}/>
                        </a>
                    </div>
                    <div className={cl.visual} data-reveal="zoom" style={{ '--reveal-delay': '200ms' }}>
                        <AdminVisual/>
                    </div>
                </div>
            </div>
        </section>
    );
}


/* === Декоративные SVG для visual-блоков === */

function MinecraftiaVisual() {
    return (
        <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg">
            <rect width="400" height="300" fill="rgba(255,255,255,0.08)" rx="14"/>
            {/* Пиксели Майнкрафт-стиля */}
            <g opacity="0.5">
                {Array.from({ length: 13 }).map((_, x) =>
                    Array.from({ length: 10 }).map((_, y) => (
                        <rect key={`${x}-${y}`} x={x*30 + 4} y={y*30 + 4}
                            width="26" height="26" rx="2"
                            fill={(x + y) % 4 === 0 ? '#67e8f9' : '#fff'}
                            opacity={(x + y) % 5 === 0 ? 0.18 : 0.05}/>
                    ))
                )}
            </g>
            {/* Большая «иконка-куб» в центре */}
            <g transform="translate(200 150)">
                <path d="M -50 -25 L 0 -50 L 50 -25 L 50 35 L 0 60 L -50 35 Z"
                    fill="#fbbf24" stroke="#1e293b" strokeWidth="3"/>
                <path d="M -50 -25 L 0 0 L 50 -25 M 0 0 L 0 60"
                    fill="none" stroke="#1e293b" strokeWidth="3"/>
                <rect x="-15" y="-15" width="14" height="14" fill="#84cc16"/>
                <rect x="1" y="-15" width="14" height="14" fill="#84cc16"/>
            </g>
        </svg>
    );
}

function RobotehnickVisual() {
    return (
        <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg">
            <rect width="400" height="300" fill="rgba(255,255,255,0.08)" rx="14"/>
            {/* Сетка breadboard */}
            <g opacity="0.4">
                {Array.from({ length: 15 }).map((_, x) =>
                    Array.from({ length: 11 }).map((_, y) => (
                        <circle key={`${x}-${y}`} cx={26 + x*25} cy={20 + y*25} r="1.6"
                            fill="#fff"/>
                    ))
                )}
            </g>
            {/* Схема: батарея — резистор — LED — Arduino */}
            <g transform="translate(50 150)">
                <rect x="0" y="0" width="44" height="28" rx="4" fill="#fbbf24"
                    stroke="#1e293b" strokeWidth="2.5"/>
                <text x="22" y="19" textAnchor="middle" fill="#1e293b"
                    fontSize="11" fontWeight="800" fontFamily="Inter">9V</text>
            </g>
            <line x1="94" y1="164" x2="160" y2="164" stroke="#fff" strokeWidth="3"/>
            <rect x="160" y="150" width="70" height="28" rx="5" fill="#92400e"/>
            {[10, 22, 34, 46, 58].map(x => (
                <rect key={x} x={160+x} y="150" width="3" height="28" fill="#fef3c7"/>
            ))}
            <line x1="230" y1="164" x2="290" y2="164" stroke="#fff" strokeWidth="3"/>
            <ellipse cx="312" cy="164" rx="22" ry="26" fill="#ef4444">
                <animate attributeName="opacity" values="1;0.35;1" dur="1.4s" repeatCount="indefinite"/>
            </ellipse>
            <ellipse cx="305" cy="156" rx="6" ry="7" fill="#fff" opacity="0.7"/>
            {/* Бегущий ток */}
            <circle r="5" fill="#fbbf24">
                <animateMotion dur="2s" repeatCount="indefinite"
                    path="M 94 164 L 160 164 L 230 164 L 312 164"/>
            </circle>
            {/* Подпись */}
            <text x="200" y="50" textAnchor="middle" fill="#fff" fontSize="14"
                fontWeight="700" fontFamily="Inter" opacity="0.85">Урок 12 · Светодиод</text>
        </svg>
    );
}

function AdminVisual() {
    return (
        <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg">
            <rect width="400" height="300" fill="rgba(255,255,255,0.08)" rx="14"/>
            {/* Каркас панели */}
            <rect x="30" y="30" width="340" height="240" rx="12" fill="#fff"/>
            <rect x="30" y="30" width="92" height="240" rx="12" fill="#0f172a"/>
            {/* Лого + меню */}
            <rect x="44" y="48" width="64" height="16" rx="3" fill="#10b981"/>
            {[84, 110, 136, 162, 188].map((y, i) => (
                <rect key={i} x="48" y={y} width={56 - i*4} height="8" rx="2"
                    fill="#475569" opacity={i === 1 ? 1 : 0.5}/>
            ))}
            {/* Заголовок */}
            <rect x="140" y="50" width="160" height="14" rx="3" fill="#0f172a"/>
            <rect x="140" y="72" width="200" height="6" rx="2" fill="#cbd5e1"/>
            {/* Три карточки статистики */}
            <rect x="140" y="98" width="68" height="46" rx="6" fill="#f8fafc" stroke="#e2e8f0"/>
            <text x="174" y="124" textAnchor="middle" fill="#3b82f6" fontSize="18"
                fontWeight="800" fontFamily="Inter">147</text>
            <text x="174" y="138" textAnchor="middle" fill="#64748b" fontSize="8"
                fontFamily="Inter">учеников</text>

            <rect x="216" y="98" width="68" height="46" rx="6" fill="#f8fafc" stroke="#e2e8f0"/>
            <text x="250" y="124" textAnchor="middle" fill="#10b981" fontSize="18"
                fontWeight="800" fontFamily="Inter">12</text>
            <text x="250" y="138" textAnchor="middle" fill="#64748b" fontSize="8"
                fontFamily="Inter">групп</text>

            <rect x="292" y="98" width="68" height="46" rx="6" fill="#f8fafc" stroke="#e2e8f0"/>
            <text x="326" y="124" textAnchor="middle" fill="#a855f7" fontSize="18"
                fontWeight="800" fontFamily="Inter">5</text>
            <text x="326" y="138" textAnchor="middle" fill="#64748b" fontSize="8"
                fontFamily="Inter">учителей</text>
            {/* Таблица */}
            <rect x="140" y="158" width="220" height="100" rx="6" fill="#f8fafc" stroke="#e2e8f0"/>
            {[174, 192, 210, 228, 246].map((y, i) => (
                <g key={y}>
                    <circle cx="156" cy={y} r="5" fill="#3b82f6" opacity={0.4 + i*0.12}/>
                    <rect x="172" y={y-3} width="100" height="6" rx="2" fill="#cbd5e1"/>
                    <rect x="288" y={y-3} width="56" height="6" rx="2" fill="#10b981" opacity="0.7"/>
                </g>
            ))}
        </svg>
    );
}
